/**
 * The Section Library.
 *
 * Every preset is ordinary canonical content: a column layout, a section
 * style, and elements from the normal registry. Nothing here is understood by
 * the renderer as a "preset" -- `presetId` is advisory only, so a section
 * inserted from this library is indistinguishable from one built by hand and
 * stays fully editable. There is deliberately no preset-specific rendering
 * path (SITES-MISSION §48).
 *
 * Copy is realistic synthetic content for a fictional Dutch church, not
 * placeholder text: a library entry whose preview reads "Lorem ipsum" teaches
 * the user nothing about what the section is for, and someone always ships it.
 */
import { Image, LayoutTemplate, Mail, Sparkles, SquareStack, Type, Users } from 'lucide-react';
import type React from 'react';

import { createBlockId } from './ids';
import type { ColumnLayout, ContentBlock, SiteSection } from './types';

export type PresetCategory = 'Hero' | 'Kaarten' | 'Over ons' | 'CTA' | 'Kerk';

export interface SectionPreset {
  id: string;
  label: string;
  description: string;
  category: PresetCategory;
  icon: React.ComponentType<{ className?: string }>;
  /** Wireframe hint for the library card. */
  preview: 'hero-glow' | 'hero-split' | 'hero-center' | 'cards-3' | 'cards-4' | 'split-left' | 'split-right' | 'cta' | 'contact';
  columnLayout: ColumnLayout;
  style: SiteSection['style'];
  /** Elements per column, in column order. */
  columns: () => ContentBlock[][];
}

const el = (type: ContentBlock['type'], text: string, extra: Partial<ContentBlock> = {}): ContentBlock =>
  ({ type, blockId: createBlockId(), text, ...extra });

const LIGHT: SiteSection['style'] = { spacing: 'spacious', colorScheme: 'light' };

export const SECTION_PRESETS: SectionPreset[] = [
  // ---------------------------------------------------------------- HERO ---
  {
    id: 'hero-modern-glow',
    label: 'Hero — Modern Glow',
    description: 'Donker verloop met gloed, badge, twee knoppen en een golf.',
    category: 'Hero',
    icon: Sparkles,
    preview: 'hero-glow',
    columnLayout: '2-1',
    style: {
      spacing: 'extra',
      minHeight: 'tall',
      colorScheme: 'dark',
      widthMode: 'content',
      verticalAlign: 'center',
      background: { mode: 'gradient', gradientFrom: 'secondary', gradientTo: 'primary', gradientDirection: 'to-bottom-right' },
      decoration: { style: 'glow', intensity: 'medium', position: 'top-right', color: 'accent' },
      bottomDivider: { style: 'wave', size: 'lg', flip: false },
      motion: 'fade-up',
    },
    columns: () => [
      [
        el('badge', 'Iedereen is welkom', { variant: 'pill' }),
        el('heading', 'Een open huis midden in de wijk', { level: 1 }),
        el('rich_text', 'Elke zondag komen we samen om te vieren, te leren en naar elkaar om te zien. Kom gerust een keer kijken — je hoeft niets mee te nemen behalve jezelf.'),
        el('feature_list', '', {
          features: [
            { text: 'Elke zondag om 10:00 uur' },
            { text: 'Koffie en ontmoeting na afloop' },
            { text: 'Kinderopvang en tienerprogramma' },
          ],
        }),
        el('button', 'Plan je bezoek', {
          label: 'Plan je bezoek',
          button: { style: 'primary', size: 'large', icon: 'arrow-right', iconPosition: 'right', hover: 'lift', shadow: 'medium' },
        }),
        el('button', 'Bekijk de diensten', {
          label: 'Bekijk de diensten',
          button: { style: 'outline', size: 'large', hover: 'accent' },
        }),
      ],
      [],
    ],
  },
  {
    id: 'hero-split',
    label: 'Hero — Split',
    description: 'Tekst naast een beeld.',
    category: 'Hero',
    icon: LayoutTemplate,
    preview: 'hero-split',
    columnLayout: '2',
    style: { spacing: 'spacious', minHeight: 'medium', colorScheme: 'light', verticalAlign: 'center' },
    columns: () => [
      [
        el('heading', 'Samen geloven, samen leven', { level: 1 }),
        el('rich_text', 'Wij zijn een gemeenschap van mensen die elkaar helpen groeien in geloof, hoop en liefde.'),
        el('button', 'Maak kennis met ons', { label: 'Maak kennis met ons', button: { style: 'primary', icon: 'arrow-right' } }),
      ],
      [el('image', '', { alt: 'Samenkomst op zondagochtend', effects: { radius: 'rounded', shadow: 'medium' } })],
    ],
  },
  {
    id: 'hero-centered',
    label: 'Hero — Gecentreerd',
    description: 'Eén krachtige boodschap in het midden.',
    category: 'Hero',
    icon: Type,
    preview: 'hero-center',
    columnLayout: '1',
    style: { spacing: 'extra', minHeight: 'medium', textAlign: 'center', colorScheme: 'light' },
    columns: () => [[
      el('badge', 'Welkom', { variant: 'accent' }),
      el('heading', 'Er is plek voor jou', { level: 1 }),
      el('rich_text', 'Of je nu al jaren meeloopt of voor het eerst binnenstapt — je hoort erbij.'),
      el('button', 'Kom langs', { label: 'Kom langs', button: { style: 'primary', size: 'large' } }),
    ]],
  },

  // -------------------------------------------------------------- KAARTEN ---
  {
    id: 'features-3',
    label: '3 kaarten',
    description: 'Drie onderwerpen naast elkaar.',
    category: 'Kaarten',
    icon: SquareStack,
    preview: 'cards-3',
    columnLayout: '1',
    style: LIGHT,
    columns: () => [[
      el('heading', 'Dit vind je bij ons', { level: 2 }),
      el('cards', '', {
        effects: { shadow: 'subtle', radius: 'rounded', hover: 'lift', border: 'none' },
        items: [
          { title: 'Samen vieren', text: 'Elke zondag een viering met muziek, stilte en een woord dat raakt.', icon: 'users' },
          { title: 'Samen groeien', text: 'Kringen en cursussen waarin je vragen mag stellen.', icon: 'star' },
          { title: 'Samen dienen', text: 'Praktische hulp voor de buurt, van maaltijd tot klusteam.', icon: 'heart' },
        ],
      }),
    ]],
  },
  {
    id: 'features-4',
    label: '4 kaarten',
    description: 'Vier korte onderwerpen op een rij.',
    category: 'Kaarten',
    icon: SquareStack,
    preview: 'cards-4',
    columnLayout: '1',
    style: { spacing: 'spacious', colorScheme: 'light', background: { mode: 'solid', color: 'surface' } },
    columns: () => [[
      el('heading', 'Waar we voor staan', { level: 2 }),
      el('cards', '', {
        effects: { shadow: 'medium', radius: 'rounded', hover: 'lift', border: 'none' },
        items: [
          { title: 'Open', text: 'Iedereen is welkom, precies zoals je bent.', icon: 'users' },
          { title: 'Betrokken', text: 'We kennen elkaar bij naam en zien naar elkaar om.', icon: 'heart' },
          { title: 'Praktisch', text: 'Geloof dat zichtbaar wordt in wat we doen.', icon: 'check' },
          { title: 'Dichtbij', text: 'Midden in de wijk, met de deur open.', icon: 'map-pin' },
        ],
      }),
    ]],
  },

  // ------------------------------------------------------------- OVER ONS ---
  {
    id: 'about-image-left',
    label: 'Afbeelding links',
    description: 'Beeld naast een verhaal.',
    category: 'Over ons',
    icon: Image,
    preview: 'split-left',
    columnLayout: '1-2',
    style: LIGHT,
    columns: () => [
      [el('image', '', { alt: 'Ons gebouw aan de vaart', effects: { radius: 'rounded', shadow: 'subtle' } })],
      [
        el('heading', 'Al vijftig jaar aan de vaart', { level: 2 }),
        el('rich_text', 'Wat begon als een klein gezelschap in een huiskamer, is uitgegroeid tot een gemeenschap van zo’n tweehonderd mensen. De deur staat nog steeds open.'),
        el('button', 'Lees ons verhaal', { label: 'Lees ons verhaal', button: { style: 'text', icon: 'arrow-right' } }),
      ],
    ],
  },
  {
    id: 'about-image-right',
    label: 'Afbeelding rechts',
    description: 'Verhaal naast een beeld.',
    category: 'Over ons',
    icon: Image,
    preview: 'split-right',
    columnLayout: '2-1',
    style: LIGHT,
    columns: () => [
      [
        el('heading', 'Geloof dat je kunt zien', { level: 2 }),
        el('rich_text', 'We geloven dat geloof pas echt wordt als het handen en voeten krijgt: in de buurt, aan tafel en in het gewone leven van elke dag.'),
        el('button', 'Wat we doen', { label: 'Wat we doen', button: { style: 'outline' } }),
      ],
      [el('image', '', { alt: 'Vrijwilligers bereiden een maaltijd', effects: { radius: 'rounded', shadow: 'subtle' } })],
    ],
  },

  // ------------------------------------------------------------------ CTA ---
  {
    id: 'cta-dark',
    label: 'CTA — Donker',
    description: 'Donkere band met een duidelijke actie.',
    category: 'CTA',
    icon: Sparkles,
    preview: 'cta',
    columnLayout: '1',
    style: {
      spacing: 'spacious',
      textAlign: 'center',
      colorScheme: 'dark',
      background: { mode: 'solid', color: 'dark' },
      decoration: { style: 'dots', intensity: 'subtle', position: 'center', color: 'primary' },
    },
    columns: () => [[
      el('heading', 'Benieuwd geworden?', { level: 2 }),
      el('rich_text', 'Loop een keer binnen op zondag, of stuur ons gerust eerst een bericht.'),
      el('button', 'Neem contact op', { label: 'Neem contact op', button: { style: 'primary', size: 'large', icon: 'mail', iconPosition: 'left' } }),
    ]],
  },
  {
    id: 'cta-light',
    label: 'CTA — Licht',
    description: 'Rustige uitnodiging op een licht vlak.',
    category: 'CTA',
    icon: Sparkles,
    preview: 'cta',
    columnLayout: '1',
    style: { spacing: 'normal', textAlign: 'center', colorScheme: 'light', background: { mode: 'solid', color: 'surface' } },
    columns: () => [[
      el('heading', 'Nieuw hier?', { level: 2 }),
      el('rich_text', 'We vertellen je graag wat je kunt verwachten op een gewone zondagochtend.'),
      el('button', 'Wat je kunt verwachten', { label: 'Wat je kunt verwachten', button: { style: 'primary' } }),
    ]],
  },

  // ----------------------------------------------------------------- KERK ---
  {
    id: 'service-times',
    label: 'Samenkomsten',
    description: 'Wanneer en waar je welkom bent.',
    category: 'Kerk',
    icon: Users,
    preview: 'cards-3',
    columnLayout: '1',
    style: LIGHT,
    columns: () => [[
      el('heading', 'Wanneer komen we samen?', { level: 2 }),
      el('cards', '', {
        effects: { shadow: 'subtle', radius: 'rounded', border: 'subtle', hover: 'none' },
        items: [
          { title: 'Zondag 10:00', text: 'Viering met muziek, gebed en uitleg. Kinderopvang aanwezig.', icon: 'calendar' },
          { title: 'Woensdag 19:30', text: 'Gebedsavond in de zijzaal. Vrij in- en uitlopen.', icon: 'calendar' },
          { title: 'Elke eerste zaterdag', text: 'Buurtmaaltijd om 17:30. Aanschuiven mag altijd.', icon: 'users' },
        ],
      }),
    ]],
  },
  {
    id: 'contact-location',
    label: 'Contact + locatie',
    description: 'Adresgegevens naast een routebeschrijving.',
    category: 'Kerk',
    icon: Mail,
    preview: 'contact',
    columnLayout: '2',
    style: { spacing: 'spacious', colorScheme: 'light', background: { mode: 'solid', color: 'surface' } },
    columns: () => [
      [
        el('heading', 'Plan je bezoek', { level: 2 }),
        el('rich_text', 'Je vindt ons aan de vaart, op loopafstand van het station. Er is gratis parkeergelegenheid achter het gebouw.'),
        el('contact', '', {
          organizationName: 'Kerk aan de Vaart',
          address: 'Vaartkade 12, 3841 AB Harderwijk',
          email: 'welkom@kerkaandevaart.nl',
          phone: '0341 - 12 34 56',
        }),
      ],
      [
        el('heading', 'Goed om te weten', { level: 3 }),
        el('feature_list', '', {
          features: [
            { text: 'Rolstoeltoegankelijk' },
            { text: 'Ringleiding aanwezig' },
            { text: 'Gratis parkeren achter het gebouw' },
          ],
        }),
        el('button', 'Route in Google Maps', {
          label: 'Route in Google Maps',
          destination: { kind: 'url', url: 'https://maps.google.com/?q=Harderwijk', newTab: true },
          button: { style: 'outline', icon: 'external' },
        }),
      ],
    ],
  },
];

export const PRESET_CATEGORIES: PresetCategory[] = ['Hero', 'Kaarten', 'Over ons', 'CTA', 'Kerk'];

/**
 * Instantiate a preset as ordinary content.
 *
 * Fresh ids throughout, every time -- inserting the same preset twice must
 * produce two independent sections, and two websites built from it must share
 * no authored identity (SITES-MISSION §53).
 */
export function instantiatePreset(preset: SectionPreset): {
  columns: Array<{ columnId: string; blocks: ContentBlock[] }>;
  columnLayout: ColumnLayout;
  style: SiteSection['style'];
  presetId: string;
} {
  return {
    columnLayout: preset.columnLayout,
    style: preset.style ? JSON.parse(JSON.stringify(preset.style)) : undefined,
    presetId: preset.id,
    columns: preset.columns().map((blocks) => ({
      columnId: createBlockId(),
      blocks: blocks.map((block) => ({ ...block, blockId: createBlockId() })),
    })),
  };
}
